/**
 * Utility functions for handling time in the Rugby Event Tracker
 */

/**
 * Format seconds to MM:SS display format
 * 
 * @param {number} seconds - Number of seconds to format
 * @returns {string} Formatted time string (MM:SS)
 */
export const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Convert MM:SS formatted time to seconds
 * 
 * @param {string} timeString - Time in MM:SS format
 * @returns {number} Total seconds
 */
export const parseTimeToSeconds = (timeString) => {
  const [minutes, seconds] = timeString.split(':').map(Number);
  return (minutes * 60) + seconds;
};

/**
 * Calculate the elapsed time between two points in seconds
 * 
 * @param {number} startSeconds - Starting time in seconds
 * @param {number} endSeconds - Ending time in seconds
 * @returns {string} Formatted elapsed time
 */
export const getElapsedTime = (startSeconds, endSeconds) => {
  const elapsed = endSeconds - startSeconds;
  if (elapsed < 0) return formatTime(0);
  return formatTime(elapsed);
};

/**
 * Get time since event occurred based on current timer
 * 
 * @param {number} eventSeconds - Time when event occurred (in seconds)
 * @param {number} currentSeconds - Current timer seconds
 * @returns {string} Formatted time since event
 */
export const getTimeSinceEvent = (eventSeconds, currentSeconds) => {
  return getElapsedTime(eventSeconds, currentSeconds);
};

/**
 * Get timestamp with date for event exports
 * 
 * @param {number} seconds - Event time in seconds
 * @param {Date} matchDate - Date of the match
 * @returns {string} Formatted timestamp
 */
export const getFullTimestamp = (seconds, matchDate = new Date()) => {
  const date = matchDate.toISOString().split('T')[0]; // Get YYYY-MM-DD
  const time = formatTime(seconds);
  return `${date} ${time}`;
};

/**
 * Calculate the standard rugby match time
 * This accounts for 40-minute halves plus any added time
 * 
 * @param {number} firstHalfSeconds - Seconds played in first half
 * @param {number} firstHalfStoppage - Stoppage time for first half in seconds
 * @param {number} secondHalfSeconds - Seconds played in second half
 * @param {number} secondHalfStoppage - Stoppage time for second half in seconds
 * @returns {string} Formatted standard match time
 */
export const getStandardMatchTime = (
  firstHalfSeconds, 
  firstHalfStoppage = 0, 
  secondHalfSeconds, 
  secondHalfStoppage = 0
) => {
  const standardHalfTime = 40 * 60; // 40 minutes in seconds
  
  // Calculate normalized position in the match
  let standardSeconds = 0;
  
  if (firstHalfSeconds <= standardHalfTime) {
    // Within standard first half time
    standardSeconds = firstHalfSeconds;
  } else if (firstHalfSeconds > standardHalfTime) {
    // First half stoppage time
    standardSeconds = standardHalfTime + Math.min(firstHalfSeconds - standardHalfTime, firstHalfStoppage);
  }
  
  if (secondHalfSeconds > 0) {
    // Add second half time
    standardSeconds = standardHalfTime + firstHalfStoppage + 
      Math.min(secondHalfSeconds, standardHalfTime);
      
    // Add second half stoppage if applicable
    if (secondHalfSeconds > standardHalfTime) {
      standardSeconds += Math.min(secondHalfSeconds - standardHalfTime, secondHalfStoppage);
    }
  }
  
  return formatTime(standardSeconds);
};

/**
 * Creates a timer controller function
 * 
 * @param {function} setSeconds - State setter for seconds
 * @param {function} setIsRunning - State setter for isRunning
 * @param {object} timerRef - Ref object to store interval
 * @returns {object} Timer control functions
 */
export const createTimerController = (setSeconds, setIsRunning, timerRef) => {
  const startTimer = () => {
    setIsRunning(true);
    timerRef.current = setInterval(() => {
      setSeconds(seconds => seconds + 1);
    }, 1000);
  };

  const pauseTimer = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
  };

  const resetTimer = () => {
    clearInterval(timerRef.current);
    setIsRunning(false);
    setSeconds(0);
  };

  const toggleTimer = (isCurrentlyRunning) => {
    if (isCurrentlyRunning) {
      pauseTimer();
    } else {
      startTimer();
    }
  };

  return {
    startTimer,
    pauseTimer,
    resetTimer,
    toggleTimer
  };
};